import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { REDIRECT_URL, SELECT_VENUE_ROUTE } from '@constants/routes';
import { FeatureFlagContext } from '@contexts/featureFlag/featureFlag-context';
import { UserContext } from '@contexts/user/user-context';
import { AuthTokenResponse, useGetTokenQueryKey } from '@hooks/auth/type';
import { useChangeUser } from '@hooks/auth/use-change-user';
import { useTokenManagement } from '@hooks/auth/use-token-management';
import { useGetUser } from '@hooks/user/user-get-user';
import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';

import { AuthContextValue } from './auth.type';

export const AuthContext = createContext<AuthContextValue>({
  isAuthenticated: false,
  isImpersonated: false,
  signOut: () => {},
  changeUser: () => {},
  resetAdminUser: () => {},
});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { getTokens, setTokens, clearTokens } = useTokenManagement();

  const { isPublicEventReportPage } = useContext(FeatureFlagContext);
  const { setUserState, setIsUserLoading } = useContext(UserContext);

  const [isAuthenticated, setIsAuthenticated] = useState(!!getTokens().accessToken);
  const [impersonateUser, setImpersonateUser] = useState<{ email: string; uuid: string }>({ email: '', uuid: '' });

  const { data: impersonateCredentials } = useChangeUser({
    uuid: impersonateUser.uuid,
    options: { enabled: !!impersonateUser.uuid && !isPublicEventReportPage },
  });

  const queryData = queryClient.getQueryData([useGetTokenQueryKey]) as AuthTokenResponse | undefined;
  const queryState = queryClient.getQueryState([useGetTokenQueryKey]);

  const data = queryData;
  const isSuccess = queryState?.status === 'success';
  const isError = queryState?.status === 'error';

  const { data: user, isInitialLoading: isUserLoading } = useGetUser({
    enabled: isAuthenticated && !!getTokens().accessToken && !!getTokens().refreshToken && !isPublicEventReportPage,
    retry: 2,
  });

  const isImpersonated =
    !!getTokens({ isImpersonate: true }).accessToken || !!getTokens({ isImpersonate: true }).refreshToken;

  const signOut = useCallback(() => {
    const currentUrl = window.location.href;
    clearTokens();
    window.location.href = `${REDIRECT_URL}=${currentUrl}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearTokens]);

  const changeUser = ({ email, uuid }: { email: string; uuid: string }) => {
    const { accessToken, refreshToken } = getTokens();
    if (accessToken && refreshToken) {
      setTokens({ accessToken, refreshToken, isImpersonate: true });
      setImpersonateUser({ email, uuid });
    }
  };

  const resetAdminUser = () => {
    const { refreshToken: adminRefreshToken, accessToken: adminAccessToken } = getTokens({ isImpersonate: true });
    if (adminAccessToken || adminRefreshToken) {
      setTokens({ accessToken: adminAccessToken ?? '', refreshToken: adminRefreshToken ?? '' });
    }
    clearTokens({ isImpersonate: true });
    window.location.reload();
  };

  useEffect(() => {
    if (impersonateCredentials) {
      setTokens({ accessToken: impersonateCredentials.accessToken, refreshToken: impersonateCredentials.refreshToken });
      window.open(SELECT_VENUE_ROUTE, '_self');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [impersonateCredentials]);

  useEffect(() => {
    if (isPublicEventReportPage) setIsUserLoading(false);
    else setIsUserLoading(isUserLoading);
    if (user) {
      Sentry.setUser({ email: user.email, id: user.id });
      setUserState(user);
      i18n.changeLanguage(user.lang);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, i18n, isUserLoading, isPublicEventReportPage]);

  useEffect(() => {
    if (isSuccess && data?.accessToken && data?.refreshToken) {
      setTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken });
      setIsAuthenticated(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, data]);

  useEffect(() => {
    if (isPublicEventReportPage) {
      setIsAuthenticated(true);
      return;
    }

    if (isError || !getTokens().refreshToken) {
      signOut();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError, isPublicEventReportPage]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        signOut,
        changeUser,
        resetAdminUser,
        isImpersonated,
      }}>
      {isPublicEventReportPage || user ? children : null}
    </AuthContext.Provider>
  );
};
